<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger.ts'
import { AddWishList, GetWishListItems, DeleteWishList } from '@/services/WishListService.ts'
import { GetFullProductImageDictionary } from '@/services/SkuService.ts'
import { ref, onMounted } from 'vue'
import { GetWishlistDetailUrl } from '@/stores/SettingsStore.ts'
import { Popup } from '@progress/kendo-vue-popup'
import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'
import { trashIcon } from '@progress/kendo-svg-icons'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import Swal from "sweetalert2";

const logger = useLogger()
setStoreLogger(logger)

const wishListName = ref('')
const showModal = ref(false)

interface IViewModal {
  wishListName: string
  aoeWishlistId: number
  skuCount: number
  images: string[]
  productCodes: string[]
}
const wishlistDetailUrl = ref<string>('')

const viewModels = ref<Array<IViewModal>>([])

LoadWishLists()

async function LoadWishLists() {
  viewModels.value = []
  try {
    const data = await GetWishListItems()
    logger.debug('GetWishListItems', data)

    // First, collect all product codes across all wishlists
    const allProductCodes: string[] = []
    const tempViewModels: IViewModal[] = []

    // Create view models with product codes
    for (const wishList of data.wishListNames) {
      const viewModel: IViewModal = {
        wishListName: wishList.wishListName,
        aoeWishlistId: wishList.aoeWishlistId,
        skuCount: 0,
        images: [],
        productCodes: [],
      }

      for (const sku of data.skus) {
        if (sku.aoeWishlistId === wishList.aoeWishlistId) {
          viewModel.skuCount++
          viewModel.productCodes.push(sku.productCode)
          if (!allProductCodes.includes(sku.productCode)) {
            allProductCodes.push(sku.productCode)
          }
        }
      }

      tempViewModels.push(viewModel)
    }

    // Fetch all images at once
    const allImageDictionary = await GetFullProductImageDictionary(allProductCodes)

    // Populate images for each view model
    for (const viewModel of tempViewModels) {
      viewModel.productCodes.forEach(productCode => {
        const imageUrl = allImageDictionary[productCode]
        if (imageUrl) {
          viewModel.images.push(imageUrl)
        }
      })
      viewModels.value.push(viewModel)
    }
  } catch (error) {
    logger.error('Error loading wishlists:', error)
  }
}


async function addWishlistClickedHandler() {
  showModal.value = false
  logger.debug('addWishlistClickedHandler')
  AddWishList(wishListName.value).then(() => {
    LoadWishLists()
  })
}

function popupCreateList() {
  showModal.value = true
}

onMounted(async () => {
  logger.debug('onMounted')
  wishlistDetailUrl.value = await GetWishlistDetailUrl()
  logger.debug('wishlistDetailUrl:', wishlistDetailUrl.value)
})

function handleImageError(event: Event) {
  // Handle image loading errors
  if (event.target instanceof HTMLImageElement) {
    event.target.style.display = 'none'
  }
}

function deleteWishListClickedHandler(wishlistName: string) {
  logger.debug('deleteWishListClickedHandler', wishlistName)
  Swal.fire({
    title: t("alerts.DeleteConfirmTitle"),
    text: t("alerts.DeleteConfirm", [
    wishlistName
    ]),
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: t("buttons.Yes"),
    cancelButtonText: t("buttons.No"),
  }).then((result) => {
    if (result.isConfirmed) {
      DeleteWishList(wishlistName).then(() => {
        viewModels.value = viewModels.value.filter((viewModel) => {
          return viewModel.wishListName !== wishlistName
        })
      })
    }
  });
}
</script>

<style lang="scss">

.wishListImage-Lead {padding:0px;}

.create-wishlist-popup-content {
    position: fixed;
    top: 50%;
    left: 50%;
    padding: $card-padding;
    transform: translate(-50%, -50%);
}

.wishlistModal-content {
  background-color: white;
  padding: $card-padding;
  border: 1px solid #888;
  width: 100%;
  border-radius: $radius-sm;
}

.wishListImage-Lead,
.wishListImage-ThumbnailList {
  width: 230px;
  height: 230px;
}

.wishListImage-Lead,
.ListCardImages-thumbnail {
  background-color: #e0e0e0;
}

.wishListImage-ThumbnailList {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  gap: $spacing-1;
}

.ListCardImages-thumbnail {
  overflow: hidden;
}

.wishListName {
  @include typography-h4;
  @include font-weight-bold;
}

.wishListItems {
  @include typography-body;
}

.imgWish {
  margin: auto;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.wishListSection {
  width: 100%;
}

.wishListCancelBtn
{
  margin-left: 10px !important;
}

.wishListDelete {
  border-radius: 50%;
}

.wishListDelete svg
{
  transform: scale(2.5);
}

.wishListCard {
  display: contents;
  width: 100%;
  height: 100%;
}

/* Mobile: one list per row, image grid scales to the available width */
@media (max-width: 767.98px) {
  .create-wishlist-popup-content {
    width: 90vw;
  }

  .wishListSection {
    align-items: center;
    margin-bottom: $spacing-4;
  }

  .wishList {
    margin-bottom: $section-margin-sm;
  }

  .wishListImageCard {
    gap: $gap-sm;
    margin-left: 0;
    margin-right: 0;
  }

  .wishListImage-Lead,
  .wishListImage-ThumbnailList {
    width: 100%;
    max-width: none;
    height: auto;
    aspect-ratio: 1 / 1;
    padding: 0;
    overflow: hidden;
  }

  .wishListImage-ThumbnailList {
    gap: $gap-sm;
  }

  .wishListName {
    margin-top: $spacing-1;
  }
}

</style>

<template>
  <div class="row wishListSection">
    <div class="col-8 col-md-9 pageHeader">
      {{ t('wishList.PageHeader') }}
    </div>
    <div class="col-4 col-md-3">
      <button @click="popupCreateList" class="btn primaryBtn">
        {{ t('wishList.CreateList') }}
      </button>
    </div>
  </div>
  <div class="row wishListContainer">
    <div
      class="col-12 col-md-6 wishList"
      v-for="viewModel in viewModels"
      :key="viewModel.aoeWishlistId">
      <KButton
        ref="btnDelete"
        class="wishListDelete"
        :svg-icon="trashIcon"
        :fill-mode="'clear'"
        @click="deleteWishListClickedHandler(viewModel.wishListName)"
        ></KButton>
      <a v-bind:href="wishlistDetailUrl + '?wishlistname=' + viewModel.wishListName" class="wishListCard">
        <div class="row wishListImageCard mt-3">
          <div class="col-12 col-md-6 wishListImage-Lead">
            <img
              v-if="viewModel.images.length > 0 && viewModel.images[0]"
              v-bind:src="viewModel.images[0]"
              :alt="t('cart.ProductImageAlt')"
              class="imgWish"
              @error="handleImageError"
            />
          </div>
          <div class="col-12 col-md-6 wishListImage-ThumbnailList">
            <div
              class="ListCardImages-thumbnail ListCardImages-thumbnail-columnone"
            >
              <img
                v-if="viewModel.images.length > 1 && viewModel.images[1]"
                v-bind:src="viewModel.images[1]"
                :alt="t('cart.ProductImageAlt')"
                class="imgWish"
                @error="handleImageError"
              />
            </div>
            <div
              class="ListCardImages-thumbnail ListCardImages-thumbnail-columntwo"
            >
              <img
                v-if="viewModel.images.length > 2 && viewModel.images[2]"
                v-bind:src="viewModel.images[2]"
                :alt="t('cart.ProductImageAlt')"
                class="imgWish"
                @error="handleImageError"
              />
            </div>
            <div
              class="ListCardImages-thumbnail ListCardImages-thumbnail-row2-columnone"
            >
              <img
                v-if="viewModel.images.length > 3 && viewModel.images[3]"
                v-bind:src="viewModel.images[3]"
                :alt="t('cart.ProductImageAlt')"
                class="imgWish"
                @error="handleImageError"
              />
            </div>
            <div
              class="ListCardImages-thumbnail ListCardImages-thumbnail-row2-columntwo"
            >
              <img
                v-if="viewModel.images.length > 4 && viewModel.images[4]"
                v-bind:src="viewModel.images[4]"
                :alt="t('cart.ProductImageAlt')"
                class="imgWish"
                @error="handleImageError"
              />
            </div>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-12 wishListName">{{ viewModel.wishListName }}</div>
        </div>
        <div class="row mt-2">
          <div class="col-12 wishListItems">{{ viewModel.skuCount }} {{ t('wishList.WishListItems') }}</div>
        </div>
      </a>
    </div>
  </div>

  <Popup
      :show="showModal"
      :popup-class="'create-wishlist-popup-content'"
    >
      <div class="wishlistModal-content">
        <div class="text-center wishListAddHeader pageHeader">{{ t('wishList.CreateList') }}</div>
        <div class="text-center wishListAddText mt-3">
          <KTextBox
            v-model="wishListName"
            :rounded="'large'"
            :theme-color="'primary'"
            :placeholder="t('wishList.PlaceHolderWishListName')"
          ></KTextBox>
        </div>
        <div class="text-center wishListAddDiv mt-3">
          <button
            class="btn primaryBtn wishListAdd"
            @click="addWishlistClickedHandler"
          >
            {{ t('wishList.CreateListButton') }}
          </button>
          <button
            class="btn secondaryBtn wishListCancelBtn"
            @click="showModal = false"
          >
            {{ t('wishList.CancelButton') }}
          </button>
        </div>
      </div>
    </Popup>
</template>
